/**
 * Secure Storage Utility
 * Provides encrypted storage for sensitive data with session management
 */

import CryptoJS from 'crypto-js';
import { safeConsole } from './piiProtection';

// Get encryption key from environment variables - NEVER hardcode in production
const ENCRYPTION_KEY = import.meta.env.VITE_ENCRYPTION_KEY || (() => {
  throw new Error('VITE_ENCRYPTION_KEY environment variable is not set. This is required for secure storage.');
})();

const SESSION_TIMEOUT_KEY = 'session_timeout';
const LAST_LOGIN_KEY = 'last_login_timestamp';

export interface SecureStorageOptions {
  encrypt?: boolean;
  sessionTimeout?: number; // in minutes
}

class SecureStorage {
  private isSessionValid(): boolean {
    const timeout = sessionStorage.getItem(SESSION_TIMEOUT_KEY);
    if (!timeout) return false;

    const timeoutDate = new Date(parseInt(timeout));
    const now = new Date();

    if (now > timeoutDate) {
      // Only clear the timeout key, not the entire session
      // This prevents clearing the auth token on a stale timeout check
      sessionStorage.removeItem(SESSION_TIMEOUT_KEY);
      return false;
    }

    return true;
  }

  private updateSessionTimeout(): void {
    const timeoutMinutes = parseInt(import.meta.env.VITE_SESSION_TIMEOUT || '60');
    const timeout = new Date();
    timeout.setMinutes(timeout.getMinutes() + timeoutMinutes);
    sessionStorage.setItem(SESSION_TIMEOUT_KEY, timeout.getTime().toString());
  }

  /**
   * Encrypts data using AES encryption
   */
  private encrypt(data: string): string {
    return CryptoJS.AES.encrypt(data, ENCRYPTION_KEY).toString();
  }

  /**
   * Decrypts data using AES decryption
   */
  private decrypt(encryptedData: string): string {
    const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  }

  /**
   * Stores data securely
   */
  setItem(key: string, value: string, options: SecureStorageOptions = {}): void {
    const { encrypt = true } = options;
    
    try {
      const dataToStore = encrypt ? this.encrypt(value) : value;
      
      // Use sessionStorage for sensitive data instead of localStorage
      sessionStorage.setItem(key, dataToStore);
      
      // Update session timeout
      this.updateSessionTimeout();
    } catch {
      safeConsole.error('Error storing secure data');
      throw new Error('Failed to store secure data');
    }
  }

  /**
   * Retrieves data securely
   */
  getItem(key: string, options: SecureStorageOptions = {}): string | null {
    const { encrypt = true } = options;
    
    if (!this.isSessionValid()) {
      return null;
    }
    
    try {
      const storedData = sessionStorage.getItem(key);
      if (!storedData) return null;
      
      // Update session timeout on access
      this.updateSessionTimeout();
      
      return encrypt ? this.decrypt(storedData) : storedData;
    } catch {
      safeConsole.error('Error retrieving secure data');
      return null;
    }
  }

  /**
   * Removes a specific item
   */
  removeItem(key: string): void {
    sessionStorage.removeItem(key);
  }

  /**
   * Clears all session data
   */
  clearSession(): void {
    sessionStorage.clear();
  }

  /**
   * Stores authentication token securely
   */
  setAuthToken(token: string): void {
    // First update session timeout to ensure session is valid
    this.updateSessionTimeout();
    // Store the token
    this.setItem('auth_token', token, { encrypt: true });
    // Mark login timestamp to prevent immediate logout
    sessionStorage.setItem(LAST_LOGIN_KEY, Date.now().toString());
  }

  /**
   * Retrieves authentication token
   */
  getAuthToken(): string | null {
    const token = this.getItem('auth_token', { encrypt: true });
    return token;
  }

  /**
   * Removes authentication token
   * @param fullClear - if true, clears all session storage (full logout). if false, only removes auth token
   */
  removeAuthToken(fullClear: boolean = true): void {
    this.removeItem('auth_token');
    sessionStorage.removeItem(LAST_LOGIN_KEY);
    // Always clear active clinic on logout (multi-branch session state)
    sessionStorage.removeItem('active_clinic_id');
    if (fullClear) {
      this.clearSession();
    }
  }

  /**
   * Checks if user session is active
   */
  hasActiveSession(): boolean {
    return this.isSessionValid() && this.getAuthToken() !== null;
  }

  /**
   * Check if login just happened (within last 5 seconds)
   * Used to prevent logout from stale 401 responses
   */
  isRecentLogin(): boolean {
    const lastLogin = sessionStorage.getItem(LAST_LOGIN_KEY);
    if (!lastLogin) return false;
    
    const loginTime = parseInt(lastLogin);
    const now = Date.now();
    const fiveSecondsInMs = 5000;
    
    return (now - loginTime) < fiveSecondsInMs;
  }

  // -----------------------------------------------------------------------
  // Multi-branch: Active Clinic (V2026.05.16)
  // -----------------------------------------------------------------------

  /**
   * Stores the clinicId the user has chosen as active for this session.
   * This is sent as X-Active-Clinic-Id header on every request.
   * Stored in sessionStorage so it resets on tab close (safer than localStorage).
   */
  setActiveClinicId(clinicId: string): void {
    sessionStorage.setItem('active_clinic_id', clinicId);
  }

  getActiveClinicId(): string | null {
    return sessionStorage.getItem('active_clinic_id');
  }

  removeActiveClinicId(): void {
    sessionStorage.removeItem('active_clinic_id');
  }
}

export const secureStorage = new SecureStorage();
